import React, { ReactNode } from 'react';
import { Construction, Package, Rocket, BarChart3, TruckIcon, Code2 } from 'lucide-react';
import { Button } from './ui/Button';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store/hooks/reduxHooks';
import { UserRole } from '../types/auth';

// Helper function for icon rendering
const renderIcon = (iconName: string, size = 60, className = '') => {
  switch (iconName) {
    case 'Construction': return <Construction size={size} className={className} />;
    case 'Package': return <Package size={size} className={className} />;
    case 'Rocket': return <Rocket size={size} className={className} />;
    case 'BarChart3': return <BarChart3 size={size} className={className} />;
    case 'TruckIcon': return <TruckIcon size={size} className={className} />;
    case 'Code2': return <Code2 size={size} className={className} />;
    default: return <Construction size={size} className={className} />;
  }
};

interface FeatureBox {
  icon: string; // Changed to string
  title: string;
  description: string;
  iconBgColor?: string;
  iconColor?: string;
}

interface UnderDevelopmentProps {
  title?: string;
  description?: string;
  primaryColor?: string;
  secondaryColor?: string;
  icon?: string; // Changed to string
  featureBoxes?: FeatureBox[];
  buttonColor?: string;
}

const UnderDevelopment: React.FC<UnderDevelopmentProps> = ({ 
  title = "Under Development",
  description = "This feature is currently under development and will be available soon. Thank you for your patience!",
  primaryColor = "indigo",
  secondaryColor = "blue",
  icon = "Construction",
  featureBoxes = [],
  buttonColor = "indigo"
}) => {
  const navigate = useNavigate();
  const { user } = useAppSelector(state => state.auth);
  
  // Get the role prefix for routes based on user role
  const getRolePrefix = () => {
    if (!user) return ''; // Default fallback
    
    switch (user.role) {
      case UserRole.ADMIN:
        return '/admin';
      case UserRole.SUPERADMIN:
        return '/superadmin';
      case UserRole.CLEVERPE_USER:
        return '/cleverpeuser';
      default:
        return '';
    }
  };
  
  // Get dashboard path based on user role
  const getDashboardPath = () => {
    return `${getRolePrefix()}/dashboard`;
  };
  
  // Navigate to the dashboard based on user role
  const handleDashboardClick = () => {
    navigate(getDashboardPath());
  };

  // Dynamic color classes based on props
  const gradientClass = `bg-gradient-to-br from-${primaryColor}-50 via-${secondaryColor}-50 to-${primaryColor}-50`;
  const iconColorClass = `text-${primaryColor}-600`;
  const titleColorClass = `text-${primaryColor}-600`;
  const tagBgClass = `bg-${primaryColor}-100`;
  const tagTextClass = `text-${primaryColor}-800`;
  const buttonBgClass = `bg-${buttonColor}-600 hover:bg-${buttonColor}-700`;

  return (
    <div className="w-full min-h-[80vh] flex items-center justify-center px-4">
      <div className="max-w-3xl w-full">
        {/* Top section with illustration and main message */}
        <div className={`${gradientClass} rounded-xl shadow-lg p-8 mb-8 relative overflow-hidden`}>
          {/* Abstract shapes for visual interest */}
          <div className={`absolute -top-12 -right-12 w-40 h-40 bg-${primaryColor}-200 opacity-20 rounded-full blur-xl`} />
          <div className={`absolute bottom-4 left-8 w-24 h-24 bg-${secondaryColor}-300 opacity-20 rounded-full blur-xl`} />
          
          <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0 bg-white p-5 rounded-full shadow-md">
              {renderIcon(icon, 60, iconColorClass)}
            </div>
            
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                {title.split(' Under Development')[0]} <span className={titleColorClass}>Under Development</span>
              </h1>
              <p className="text-gray-600 mb-4">
                {description}
              </p>
              
              <div className={`inline-flex items-center ${tagBgClass} ${tagTextClass} px-3 py-1 rounded-full text-sm font-medium`}>
                <Construction size={14} className="mr-1" />
                Coming Soon
              </div>
            </div>
          </div>
        </div>
        
        {/* Feature preview boxes */}
        {featureBoxes.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {featureBoxes.map((box, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
                <div className={`w-10 h-10 rounded-full ${box.iconBgColor || `bg-${primaryColor}-100`} flex items-center justify-center mb-4`}>
                  {renderIcon(box.icon, 20, box.iconColor || `text-${primaryColor}-600`)}
                </div>
                <h3 className="font-bold text-gray-800 mb-2">{box.title}</h3>
                <p className="text-gray-600 text-sm">
                  {box.description}
                </p>
              </div>
            ))}
          </div>
        )}
        
        {/* Newsletter/Update subscription */}
        <div className="bg-white rounded-lg p-4 shadow-md border border-gray-100">
          <p className="text-stone-600 mb-4">
            We'll let you know as soon as this feature is available.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={() => window.history.back()}
              variant="outline"
              className="flex-1"
            >
              Go Back
            </Button>
            <Button 
              onClick={handleDashboardClick}
              className={`flex-1 ${buttonBgClass}`}
            >
              Check Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnderDevelopment; 